import React from 'react';
import { motion } from 'framer-motion';
import { 
  Network, Database, Code, Shield, Zap, Cloud, 
  Layers, Building, Smartphone, Users, Lock, 
  ArrowRight, Play, CheckCircle, Globe
} from 'lucide-react';

const TechnicalArchitecture = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left text-white"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white mb-6">
                <Network className="h-10 w-10" />
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                Technical Architecture
                <span className="block text-3xl lg:text-4xl font-normal mt-2 text-blue-200">
                  Enterprise-Grade Infrastructure
                </span>
              </h1>
              <p className="text-xl lg:text-2xl text-white/80 mb-10 leading-relaxed">
                Explore our robust platform architecture, system diagrams, and technical implementation 
                that powers enterprise applications worldwide.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href="https://app.aquargin.com/register"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Explore Architecture
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.a>
                
                <motion.a
                  href="https://www.youtube.com/@nocodeAquargin"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Tech Demo
                </motion.a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src="/images/Network_diagram_showing_Aquargin_platform_at_cente_1750596522.png"
                  alt="Aquargin Platform Network Architecture"
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* System Architecture Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              System Architecture Overview
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive view of our platform's technical architecture and system design
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h3 className="text-2xl font-bold text-gray-900">Platform Architecture</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our modular, scalable architecture ensures enterprise-grade performance, 
                security, and reliability. Built on modern cloud infrastructure with 
                microservices design patterns.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Layers, title: "Microservices Architecture", desc: "Distributed, scalable service design" },
                  { icon: Cloud, title: "Cloud-Native Infrastructure", desc: "Multi-cloud deployment capabilities" },
                  { icon: Database, title: "High-Performance Databases", desc: "Optimized data storage and retrieval" },
                  { icon: Shield, title: "Security First", desc: "End-to-end encryption and protection" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="overflow-hidden rounded-lg shadow-xl">
                <img
                  src="/images/System_architecture_diagram_showing_Aquargin_platf_1750596258.png"
                  alt="System Architecture Diagram"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Technical Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Advanced technical features and implementation details
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg mb-6">
                <img
                  src="/images/Clean_modern_API_documentation_interface_showing_c_1750596225.png"
                  alt="API Documentation Interface"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">API-First Architecture</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive REST APIs with detailed documentation and SDKs for seamless integration
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>RESTful API Design</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>OpenAPI Documentation</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>SDK Libraries</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg mb-6">
                <img
                  src="/images/Modern_smartphone_displaying_Aquargin_mobile_app_i_1750596554.png"
                  alt="Mobile Application"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Mobile-First Design</h3>
              <p className="text-gray-600 mb-4">
                Native mobile applications for iOS and Android with offline capabilities
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>Native iOS & Android</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>Offline Sync</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>Push Notifications</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg mb-6">
                <img
                  src="/images/LEGO-like_building_blocks_floating_in_space_conne_1750595831.png"
                  alt="Modular Components"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Modular Components</h3>
              <p className="text-gray-600 mb-4">
                LEGO-like building blocks that connect seamlessly for rapid application development
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>Reusable Components</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>Drag & Drop Interface</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  <span>Custom Extensions</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Future Technology Vision */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="overflow-hidden rounded-lg shadow-xl">
                <img
                  src="/images/Forward-looking_visualization_of_emerging_technolo_1750596619.png"
                  alt="Future Technology Vision"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h3 className="text-2xl font-bold text-gray-900">Future Technology Roadmap</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                We're constantly evolving our platform with emerging technologies to stay 
                ahead of the curve and provide cutting-edge solutions for our enterprise clients.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Code, title: "Advanced AI Integration", desc: "Next-gen machine learning capabilities" },
                  { icon: Globe, title: "Edge Computing", desc: "Distributed processing for faster response" },
                  { icon: Zap, title: "Quantum-Ready Architecture", desc: "Future-proof quantum computing support" },
                  { icon: Lock, title: "Zero-Trust Security", desc: "Advanced security paradigms" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Technical Images */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Implementation & Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional services and support infrastructure
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg mb-4">
                <img
                  src="/images/Professional_webinar_setup_with_presenter_giving_d_1750596455.png"
                  alt="Professional Training & Webinars"
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Training & Webinars</h3>
              <p className="text-gray-600">Comprehensive training programs and regular webinars</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg mb-4">
                <img
                  src="/images/Professional_business_meeting_with_client_represen_1750596292.png"
                  alt="Client Consultation"
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Client Consultation</h3>
              <p className="text-gray-600">Strategic consulting and implementation planning</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center group"
            >
              <div className="overflow-hidden rounded-lg shadow-lg mb-4">
                <img
                  src="/images/Modern_customer_service_center_with_support_repres_1750596157.png"
                  alt="Customer Support Center"
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-gray-600">Round-the-clock technical support and assistance</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Explore Our Technical Architecture?
            </h2>
            <p className="text-xl mb-8">
              Get a detailed technical walkthrough and see how our platform can power your enterprise applications.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://app.aquargin.com/register"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Technical Demo
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.a>
              
              <motion.a
                href="https://www.youtube.com/@nocodeAquargin"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors duration-300 flex items-center justify-center group"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Architecture Videos
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TechnicalArchitecture;